import {
  TabList,
  Tab,
  Badge,
  Button,
  Subtitle2,
  Tooltip,
  makeStyles,
  tokens,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
} from '@fluentui/react-components';
import type { SelectTabEventHandler } from '@fluentui/react-components';
import { ArrowLeft16Regular } from '@fluentui/react-icons';
import { ottoTokens } from '@/theme/ottoTokens';
import type { RunStatus } from '@/types/monitoring';
import { DraftBadge } from './DraftBadge';
import { PublishButton } from './PublishButton';
import { AutosavePill } from './AutosavePill';
import { AutosaveStatus } from './AutosaveStatus';
import { ManualSaveButton } from './ManualSaveButton';
import { WorkflowOverflowMenu } from './WorkflowOverflowMenu';
import { useDraftModeStore, selectIsDraftMode } from '@/stores/draftModeStore';
import { useGuardedModeSwitch } from '@/hooks/useGuardedModeSwitch';

export interface WorkflowViewTabsProps {
  activeTab: 'designer' | 'monitoring';
  onTabChange: (tab: 'designer' | 'monitoring') => void;
  runCount?: number;
  latestRunStatus?: RunStatus;
  /** Display name of the current workflow, shown on the left of the sub-header. */
  workflowDisplayName?: string;
  /**
   * URL workflow name (used by the Publish button). When omitted, the
   * Publish button is hidden.
   */
  workflowName?: string;
  /** Trigger proactive validation from the overflow menu. */
  onValidate?: () => void;
  /** Current validation error count — surfaced in the menu label when > 0. */
  validationErrorCount?: number;
  /** Whether validation is currently running. */
  isValidating?: boolean;
  /**
   * Optional back-navigation handler shown as an arrow to the left of the
   * workflow name (mirrors EntityOverviewHeader). Omit to hide the button.
   */
  onBack?: () => void;
  /**
   * Accessible name + tooltip for the back button. Defaults to "Back" but
   * callers SHOULD pass a destination-aware label (e.g., "Back to workflows").
   */
  backLabel?: string;
}

const useStyles = makeStyles({
  container: {
    position: 'relative',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingInline: tokens.spacingHorizontalL,
    paddingBlock: tokens.spacingVerticalS,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    minHeight: '48px',
  },
  leftSlot: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  backButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground2,
    transitionProperty: 'transform',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    ':active': {
      transform: 'scale(0.96)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':active': {
        transform: 'none',
      },
    },
  },
  workflowName: {
    // Spec calls for the Subtitle2 component at 14px (overriding Fluent's
    // default 16px) with semibold weight and primary foreground.
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Absolutely centered in the header so the tabs sit at the geometric
  // midpoint regardless of left/right slot widths (per spec). The left
  // and right slots reserve space via flex:1 and the tabs float above
  // them. zIndex lifts the tabs above the right slot, which is rendered
  // after centerSlot in DOM order and would otherwise intercept pointer
  // events on the right half of the tabs (its flex container spans the
  // right ~50% of the header even though its content sits at flex-end).
  centerSlot: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1,
    display: 'flex',
    justifyContent: 'center',
    flexShrink: 0,
  },
  rightSlot: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  tabContent: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  badge: {
    fontVariantNumeric: 'tabular-nums',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  publishedBanner: {
    borderTop: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: 0,
  },
});

const STATUS_DOT_COLORS: Partial<Record<RunStatus, string>> = {
  running: ottoTokens.statusRunning,
  failed: ottoTokens.statusFailed,
  cancelled: ottoTokens.statusCancelled,
};

function buildMonitoringAriaLabel(runCount?: number, latestRunStatus?: RunStatus): string {
  const parts = ['Monitoring'];
  if (runCount !== undefined && runCount > 0) {
    parts.push(`${runCount} ${runCount === 1 ? 'run' : 'runs'}`);
  }
  if (latestRunStatus && latestRunStatus !== 'succeeded') {
    parts.push(`last run ${latestRunStatus}`);
  }
  return parts.join(', ');
}

export const WorkflowViewTabs = ({
  activeTab,
  onTabChange,
  runCount,
  latestRunStatus,
  workflowDisplayName,
  workflowName,
  onValidate,
  validationErrorCount,
  isValidating = false,
  onBack,
  backLabel = 'Back',
}: WorkflowViewTabsProps) => {
  const styles = useStyles();
  // Read isDraftMode from the store. WorkflowViewTabs is rendered above
  // DraftModeProvider in the tree, so context isn't available — but the
  // provider keeps this store in sync regardless.
  const isDraftMode = useDraftModeStore(selectIsDraftMode);
  const modeSwitch = useGuardedModeSwitch(workflowName ? { workflowName } : {});

  const handleTabSelect: SelectTabEventHandler = (_event, data) => {
    if (data.value === 'designer' || data.value === 'monitoring') {
      onTabChange(data.value);
    }
  };

  const statusDotColor = latestRunStatus ? STATUS_DOT_COLORS[latestRunStatus] : undefined;

  return (
    <>
      <div className={styles.container}>
        <div className={styles.leftSlot}>
        {onBack && (
          <Tooltip content={backLabel} relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<ArrowLeft16Regular />}
              className={styles.backButton}
              aria-label={backLabel}
              onClick={onBack}
            />
          </Tooltip>
        )}
        {workflowDisplayName && (
          <Subtitle2 className={styles.workflowName} title={workflowDisplayName}>
            {workflowDisplayName}
          </Subtitle2>
        )}
        <DraftBadge isDraftMode={isDraftMode} appearance="outline" />
        </div>
        <div className={styles.centerSlot}>
        <TabList selectedValue={activeTab} onTabSelect={handleTabSelect}>
          <Tab value="designer" id="tab-designer" aria-label="Designer">
            Designer
          </Tab>
          <Tab value="monitoring" id="tab-monitoring" aria-label={buildMonitoringAriaLabel(runCount, latestRunStatus)}>
            <span className={styles.tabContent}>
              Monitoring
              {runCount !== undefined && runCount > 0 && (
                <Badge
                  className={styles.badge}
                  appearance="tint"
                  color="informative"
                  size="small"
                  aria-hidden="true"
                >
                  {runCount}
                </Badge>
              )}
              {statusDotColor && (
                <span
                  className={styles.statusDot}
                  style={{ backgroundColor: statusDotColor }}
                  aria-hidden="true"
                />
              )}
            </span>
          </Tab>
        </TabList>
        </div>
        <div className={styles.rightSlot}>
        {/* Save-related affordances, ordered left-to-right as a small
            narrative: state ("Autosaved X ago") → configuration ("Autosave:
            30s") → manual fallback (only when autosave is off) → publish
            → overflow menu. All only apply while authoring — hidden on the
            Monitoring tab. */}
        {activeTab === 'designer' && <AutosaveStatus />}
        {activeTab === 'designer' && <AutosavePill />}
        {activeTab === 'designer' && (
          <ManualSaveButton {...(workflowName && { workflowName })} />
        )}
        {activeTab === 'designer' && workflowName && <PublishButton workflowName={workflowName} />}
        {activeTab === 'designer' && (
          <WorkflowOverflowMenu
            {...(onValidate && { onValidate })}
            {...(validationErrorCount !== undefined && { validationErrorCount })}
            isValidating={isValidating}
            {...(workflowName && { workflowName })}
          />
        )}
        </div>
      </div>
      {activeTab === 'designer' && !isDraftMode && (
        <MessageBar intent="info" layout="singleline" className={styles.publishedBanner}>
          <MessageBarBody>
            <MessageBarTitle>Published version</MessageBarTitle>
            This view is read-only. Switch to draft to make changes.
          </MessageBarBody>
          <MessageBarActions>
            <Button appearance="transparent" size="small" onClick={modeSwitch.switchToDraft}>
              Edit draft
            </Button>
          </MessageBarActions>
        </MessageBar>
      )}
    </>
  );
};
